import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, CardTitle, CardFooter } from 'react-bootstrap';
import { Tabs, Tab } from 'react-bootstrap';
import { Badge, Button, Row, Col, Table, FormControl, FormLabel, Form } from 'react-bootstrap';
import Navbar from '../../../../Navbar/Navbar';
import { 
  FaDownload, 
  FaPrint, 
  FaFilter, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaClock, 
  FaStore, 
  FaWeight, 
  FaRupeeSign, 
  FaHistory, 
  FaLayerGroup, 
  FaCog, 
  FaUndo
} from 'react-icons/fa';
import axios from 'axios';
import baseURL from '../../../../Url/NodeBaseURL';

// Brand colors matching your existing theme
const brand = {
  primary: "#8B5F2A",
  secondary: "#FFF8E6",
};

// Utility function for number formatting
const number = (n) => (n ?? 0).toLocaleString("en-IN");

// Safe number formatting function
const safeNumber = (value, decimals = 2) => {
  const num = parseFloat(value) || 0;
  return num.toFixed(decimals);
};

// Safe integer formatting function
const safeInteger = (value) => {
  return parseInt(value) || 0;
};

// Component for section titles
function SectionTitle({ title, right }) {
  return (
    <div className="d-flex justify-content-between align-items-center mb-2">
      <h6 className="mb-0 fw-semibold" style={{ color: brand.primary }}>{title}</h6>
      {right}
    </div>
  );
}


// Component for status pills
function StatusPill({ ok = true, label }) {
  return (
    <Badge 
      bg={ok ? "success" : "danger"} 
      className="d-flex align-items-center gap-1"
    >
      {ok ? <FaCheckCircle /> : <FaExclamationTriangle />} {label}
    </Badge>
  );
}

// Icon component
const Icon = ({ name }) => {
  const common = "me-1";
  switch (name) {
    case "rupee":
      return <FaRupeeSign className={common} />;
    case "layers":
      return <FaLayerGroup className={common} />;
    case "alert":
      return <FaExclamationTriangle className={common} />;
    case "weight":
      return <FaWeight className={common} />;
    case "history":
      return <FaHistory className={common} />;
    case "clock":
      return <FaClock className={common} />;
    default:
      return <FaStore className={common} />;
  }
};

const EODDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    salesSummary: [],
    kpiTotals: {},
    tenderSummary: [],
    salesReturns: [],
    exceptions: [],
    actionLog: [],
    urdSummary: [],
    chitSummary: [],
    advanceSummary: [],
    customerBalance: [],
    reconciliation: {},
    refunds: {}
  });

  // Fetch dashboard data with individual error handling
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const currentDate = dateRange.from;
    const failedEndpoints = [];
    const successfulData = {};

    // Define all API endpoints to test
    const apiEndpoints = [
      { key: 'salesSummary', url: `${baseURL}/get/sales-summary-by-metal?date=${currentDate}`, dataKey: 'salesSummaryByMetal' },
      { key: 'kpiTotals', url: `${baseURL}/get/main-kpi-totals?date=${currentDate}`, dataKey: 'kpiTotals' },
      { key: 'salesReturns', url: `${baseURL}/get/sales-returns?date_from=${currentDate}&date_to=${currentDate}`, dataKey: 'salesReturns' },
      { key: 'exceptions', url: `${baseURL}/get/exceptions?date=${currentDate}`, dataKey: 'exceptions' },
      { key: 'activityLog', url: `${baseURL}/get/activity-log?date_from=${currentDate}&date_to=${currentDate}`, dataKey: 'activityLog' },
      { key: 'urdSummary', url: `${baseURL}/get/urd-summary?date=${currentDate}`, dataKey: 'urdSummary' },
      { key: 'chitSummary', url: `${baseURL}/get/chit-summary?date=${currentDate}`, dataKey: 'chitSummary' },
      { key: 'advanceSummary', url: `${baseURL}/get/advance-summary?date=${currentDate}`, dataKey: 'advanceSummary' },
      { key: 'customerBalance', url: `${baseURL}/get/customer-balances?date=${currentDate}`, dataKey: 'customerBalance' },
      { key: 'reconciliation', url: `${baseURL}/get/daily-reconciliation?date=${currentDate}`, dataKey: null },
      { key: 'refunds', url: `${baseURL}/get/refunds?date=${currentDate}`, dataKey: 'refunds' }
    ];

    // Test each endpoint individually
    for (const endpoint of apiEndpoints) {
      try {
        console.log(`Testing endpoint: ${endpoint.url}`);
        const response = await axios.get(endpoint.url);
        successfulData[endpoint.key] = endpoint.dataKey ? response.data[endpoint.dataKey] : response.data;
        console.log(`✅ Success: ${endpoint.key}`);
      } catch (error) {
        console.error(`❌ Failed: ${endpoint.key} - ${error.message}`);
        failedEndpoints.push({
          endpoint: endpoint.key,
          url: endpoint.url,
          error: error.message,
          status: error.response?.status || 'No response'
        });
      }
    }

    // Try to fetch existing endpoints that we know work
    try {
      const paymentsResponse = await axios.get(`${baseURL}/get/payments`);
      successfulData.tenderSummary = processTenderSummary(paymentsResponse.data?.payments || []);
      console.log('✅ Success: payments (existing endpoint)');
    } catch (error) {
      console.error('❌ Failed: payments -', error.message);
      failedEndpoints.push({
        endpoint: 'payments',
        url: `${baseURL}/get/payments`,
        error: error.message,
        status: error.response?.status || 'No response'
      });
    }

    // Try to fetch sales data from existing endpoint
    try {
      const salesResponse = await axios.get(`${baseURL}/get-unique-repair-details`);
      console.log('🔍 Raw sales response:', salesResponse.data);
      
      const salesData = salesResponse.data.filter(
        item => item.transaction_status === 'Sales' || 
                item.transaction_status === "ConvertedInvoice"
      );
      
      console.log('🔍 Filtered sales data:', salesData);
      console.log('🔍 Sales data length:', salesData.length);
      
      // Calculate KPI totals from real sales data
      const calculatedKPIs = calculateKPIsFromSalesData(salesData, successfulData.tenderSummary || []);
      successfulData.kpiTotals = calculatedKPIs;
      console.log('✅ Success: sales data (existing endpoint) - KPIs calculated');
    } catch (error) {
      console.error('❌ Failed: sales data -', error.message);
      failedEndpoints.push({
        endpoint: 'salesData',
        url: `${baseURL}/get-unique-repair-details`,
        error: error.message,
        status: error.response?.status || 'No response'
      });
    }
    const processRefunds = (refundData) => {
      if (!Array.isArray(refundData)) return { Total: 0 };

      const refundSummary = refundData.reduce((acc, item) => {
        if (Array.isArray(item.refunds)) {
          item.refunds.forEach((r) => {
            const mode = r.method || "Unknown";
            const amt = parseFloat(r.amount) || 0;
            acc[mode] = (acc[mode] || 0) + amt;
            acc.Total = (acc.Total || 0) + amt; 
          });
        }
        return acc;
      }, { Total: 0 });
      return refundSummary;
  };

   successfulData.refunds = processRefunds(successfulData.refunds);

    // Set dashboard data with successful results and fallbacks
    setDashboardData({
      salesSummary: successfulData.salesSummary || processSalesSummary([]),
      kpiTotals: successfulData.kpiTotals || {},
      salesReturns: successfulData.salesReturns || processSalesReturns([]),
      exceptions: successfulData.exceptions || processExceptions([]),
      actionLog: successfulData.activityLog || processActionLog([], []),
      urdSummary: successfulData.urdSummary || processURDSummary([]),
      chitSummary: successfulData.chitSummary || processChitSummary(),
      advanceSummary: successfulData.advanceSummary || processAdvanceSummary(),
      customerBalance: successfulData.customerBalance || processCustomerBalance(),
      tenderSummary: successfulData.tenderSummary || [],
      reconciliation: successfulData.reconciliation || {},
      refunds: successfulData.refunds || processRefunds([])
    });

    // Set error message with details about failed endpoints
    if (failedEndpoints.length > 0) {
      const errorMessage = `Failed to load data from ${failedEndpoints.length} endpoint(s):\n${failedEndpoints.map(f => `• ${f.endpoint}: ${f.status} - ${f.error}`).join('\n')}`;
      setError(errorMessage);
      console.error('Failed endpoints:', failedEndpoints);
    }

    setLoading(false);
  }, [dateRange.from]);

  // Calculate KPIs from real sales data
  const calculateKPIsFromSalesData = (salesData, tenderSummary) => {
    const currentDate = dateRange.from;
    
    console.log('🔍 Debugging sales data calculation:');
    console.log('Current date:', currentDate);
    console.log('Total sales data received:', salesData.length);
    console.log('Sample sales record:', salesData[0]);
    
    // Show all available dates in the data
    const availableDates = [...new Set(salesData.map(sale => new Date(sale.date).toISOString().split('T')[0]))];
    console.log('📅 Available dates in sales data:', availableDates);
    
    // Filter sales data for current date
    const todaySales = salesData.filter(sale => {
      const saleDate = new Date(sale.date).toISOString().split('T')[0];
      return saleDate === currentDate;
    });

    console.log('Sales for today:', todaySales.length);
    console.log('Today sales sample:', todaySales[0]);
    
    // If no sales for today, show sales from the most recent date
    if (todaySales.length === 0 && salesData.length > 0) {
      const mostRecentDate = availableDates.sort().pop();
      console.log('⚠️ No sales for selected date, using most recent date:', mostRecentDate);
      const recentSales = salesData.filter(sale => {
        const saleDate = new Date(sale.date).toISOString().split('T')[0];
        return saleDate === mostRecentDate;
      });
      console.log('Recent sales count:', recentSales.length);
      return calculateKPIsFromRecentSales(recentSales, tenderSummary, mostRecentDate);
    }

    // Calculate totals
    const totalOrders = todaySales.length;
    
    // Try different field names for gross sales
    const grossSales = todaySales.reduce((sum, sale) => {
      const amount = parseFloat(sale.net_amount) || parseFloat(sale.total_amount) || parseFloat(sale.amount) || parseFloat(sale.final_amount) || 0;
      console.log('Sale amount calculation:', {
        net_amount: sale.net_amount,
        total_amount: sale.total_amount,
        amount: sale.amount,
        final_amount: sale.final_amount,
        calculated: amount
      });
      return sum + amount;
    }, 0);
    
    // Calculate receipts from tender summary
    const receiptsTotal = tenderSummary.reduce((sum, tender) => sum + (parseFloat(tender.amount) || 0), 0);
    
    // For now, use mock values for refunds (until we have real refund data)
    const totalRefunded = 25500; // This would come from a refunds API
    const netCollections = receiptsTotal - totalRefunded;

    console.log('Calculated KPIs:', {
      totalOrders,
      grossSales,
      receiptsTotal,
      totalRefunded,
      netCollections
    });

    return {
      totalOrders,
      grossSales,
      receiptsTotal,
      totalRefunded,
      netCollections
    };
  };

  // Helper function to calculate KPIs from recent sales when no sales for selected date
  const calculateKPIsFromRecentSales = (recentSales, tenderSummary, dateUsed) => {
    console.log(`🔍 Calculating KPIs from ${dateUsed} (${recentSales.length} sales)`);
    
    const totalOrders = recentSales.length;
    
    // Try different field names for gross sales
    const grossSales = recentSales.reduce((sum, sale) => {
      const amount = parseFloat(sale.net_amount) || parseFloat(sale.total_amount) || parseFloat(sale.amount) || parseFloat(sale.final_amount) || 0;
      console.log('Recent sale amount calculation:', {
        net_amount: sale.net_amount,
        total_amount: sale.total_amount,
        amount: sale.amount,
        final_amount: sale.final_amount,
        calculated: amount
      });
      return sum + amount;
    }, 0);
    
    // Calculate receipts from tender summary
    const receiptsTotal = tenderSummary.reduce((sum, tender) => sum + (parseFloat(tender.amount) || 0), 0);
    
    // For now, use mock values for refunds (until we have real refund data)
    const totalRefunded = 25500; // This would come from a refunds API
    const netCollections = receiptsTotal - totalRefunded;

    console.log('Calculated KPIs from recent sales:', {
      totalOrders,
      grossSales,
      receiptsTotal,
      totalRefunded,
      netCollections,
      dateUsed
    });

    return {
      totalOrders,
      grossSales,
      receiptsTotal,
      totalRefunded,
      netCollections
    };
  };

  // Test individual endpoints for debugging
  const testEndpoints = useCallback(async () => {
    console.log('🔍 Testing all API endpoints...');
    const currentDate = dateRange.from;
    
    const endpoints = [
      { name: 'Sales Summary', url: `${baseURL}/get/sales-summary-by-metal?date=${currentDate}` },
      { name: 'KPI Totals', url: `${baseURL}/get/main-kpi-totals?date=${currentDate}` },
      { name: 'Sales Returns', url: `${baseURL}/get/sales-returns?date_from=${currentDate}&date_to=${currentDate}` },
      { name: 'Exceptions', url: `${baseURL}/get/exceptions?date=${currentDate}` },
      { name: 'Activity Log', url: `${baseURL}/get/activity-log?date_from=${currentDate}&date_to=${currentDate}` },
      { name: 'URD Summary', url: `${baseURL}/get/urd-summary?date=${currentDate}` },
      { name: 'Chit Summary', url: `${baseURL}/get/chit-summary?date=${currentDate}` },
      { name: 'Advance Summary', url: `${baseURL}/get/advance-summary?date=${currentDate}` },
      { name: 'Customer Balances', url: `${baseURL}/get/customer-balances?date=${currentDate}` },
      { name: 'Daily Reconciliation', url: `${baseURL}/get/daily-reconciliation?date=${currentDate}` },
      { name: 'Payments (Existing)', url: `${baseURL}/get/payments` }
    ];

    for (const endpoint of endpoints) {
      try {
        console.log(`Testing: ${endpoint.name}`);
        const response = await axios.get(endpoint.url);
        console.log(`✅ ${endpoint.name}: SUCCESS`, response.data);
      } catch (error) {
        console.error(`❌ ${endpoint.name}: FAILED`, {
          status: error.response?.status,
          message: error.message,
          url: endpoint.url
        });
      }
    }
  }, [dateRange.from]);

  // Process sales summary by metal/stone type
  const processSalesSummary = (salesData) => {
    // Mock data to match screenshot exactly
    return [
      { metal: "Gold 22K", grossWt: 171.04, netWt: 167.12, orders: 21 },
      { metal: "Silver 92.5", grossWt: 994.4, netWt: 994.4, orders: 12 },
      { metal: "Diamonds", grossWt: 0.0, netWt: 0.0, orders: 7 },
      { metal: "Gemstones", grossWt: 0.0, netWt: 0.0, orders: 9 }
    ];
  };

  // Process tender summary
  const processTenderSummary = (paymentsData) => {
    const summary = {};
    
    paymentsData.forEach(payment => {
      const mode = payment.mode || 'Cash';
      if (!summary[mode]) {
        summary[mode] = { mode, amount: 0 };
      }
      summary[mode].amount += parseFloat(payment.cash_amt || 0);
    });

    return Object.values(summary);
  };

  // Process sales returns (mock data for now)
  const processSalesReturns = (salesData) => {
    // This would need a dedicated endpoint for sales returns
    return [
      { srNo: "SR/GOLD/00023", refNo: "INV/GOLD/00391", type: "Gold Bracelet 22K", grossWt: 18.22, netWt: 18.12, amount: 12500, refundMode: "Card", refundAmount: 12500, status: "Refunded" },
      { srNo: "SR/EMR/00007", refNo: "INV/STONE/00118", type: "Emerald Pendant", grossWt: 0.00, netWt: 0.00, amount: 9800, refundMode: "Card", refundAmount: 9800, status: "Refunded" },
      { srNo: "SR/SIL/00009", refNo: "INV/SIL/00271", type: "Silver Anklet", grossWt: 42.10, netWt: 42.10, amount: 3200, refundMode: "Cash", refundAmount: 3200, status: "Refunded" }
    ];
  };

  // Process exceptions
  const processExceptions = (salesData) => {
    // Mock data to match screenshot
    return [
      {
        id: 1,
        type: "Discount > 15%",
        ref: "INV/DIAM/01245",
        by: "Anita",
        note: "0.48 ct VS studs – festival coupon + manual extra",
        severity: "high"
      },
      {
        id: 2,
        type: "Manual price override",
        ref: "INV/GOLD/00412",
        by: "Rohit",
        note: "22K bangle – price updated vs rate @ 6650/gm",
        severity: "medium"
      },
      {
        id: 3,
        type: "Return without weight verify",
        ref: "SR/EMR/00007",
        by: "Frontdesk",
        note: "Emerald pendant – skipped second weigh",
        severity: "high"
      },
      {
        id: 4,
        type: "Negative stock",
        ref: "SKU-DIA-RING-071",
        by: "System",
        note: "Halo ring size 12 went -1 after exchange",
        severity: "medium"
      }
    ];
  };

  // Process action log
  const processActionLog = (salesData, paymentsData) => {
    const actions = [];
    
    // Add sales actions
    salesData.slice(0, 5).forEach(sale => {
      actions.push({
        t: new Date(sale.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        user: sale.account_name || 'System',
        role: 'Sales',
        kind: 'Sale',
        ref: sale.invoice_number,
        desc: `Sold ${sale.metal_type || 'Gold'} ${sale.net_weight || 0}g for ₹${number(sale.net_amount || 0)}`,
        icon: 'rupee'
      });
    });

    // Add payment actions
    paymentsData.slice(0, 3).forEach(payment => {
      actions.push({
        t: new Date(payment.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        user: payment.account_name || 'Cashier',
        role: 'Cashier',
        kind: 'Payment',
        ref: payment.receipt_no,
        desc: `Payment received ₹${number(payment.cash_amt || 0)} via ${payment.mode}`,
        icon: 'rupee'
      });
    });

    return actions.sort((a, b) => b.t.localeCompare(a.t));
  };

  // Process URD summary
  const processURDSummary = (purchasesData) => {
    // Mock data to match screenshot
    return [
      { rate: "8.4g @ 60%", mWeight: 14.0, stnWt: 0.0, netWt: 14.0, eqWt: 8.4, amount: 979.0 },
      { rate: "@ 60%", mWeight: 14.0, stnWt: 0.0, netWt: 14.0, eqWt: 8.4, amount: 979.0 }
    ];
  };

  // Process chit summary (mock data)
  const processChitSummary = () => {
    return [
      { label: "Opening Chit Balance", amount: 734500 },
      { label: "Days Chit Collection", amount: 0 },
      { label: "Days Chit Payment", amount: 0 },
      { label: "Bonus Adjusted", amount: 0 },
      { label: "Chit Closing", amount: 734500 }
    ];
  };

  // Process advance summary (mock data)
  const processAdvanceSummary = () => {
    return [
      { label: "Opening Advance", amount: 916787 },
      { label: "Advance Receive", amount: 0 },
      { label: "Advance Issue", amount: 9500 },
      { label: "Closing Advance", amount: 907287 }
    ];
  };

  // Process customer balance (mock data)
  const processCustomerBalance = () => {
    return [
      { label: "Balance Received", amount: 18000 },
      { label: "Discount Given", amount: 0 },
      { label: "Bal Payable", amount: 0 },
      { label: "Paid", amount: 0 },
      { label: "Closing Balance", amount: 58739 }
    ];
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Calculate totals from API data
  const receiptsTotal = dashboardData.kpiTotals.receiptsTotal || 0;
  const totalRefunded = dashboardData.kpiTotals.totalRefunded || 0;
  const netCollections = dashboardData.kpiTotals.netCollections || 0;
  const totalOrders = dashboardData.kpiTotals.totalOrders || 0;
  const grossSales = dashboardData.kpiTotals.grossSales || 0;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">⚠️ Dashboard Data Status</h4>
          <p className="mb-3">Some API endpoints are not available yet. The dashboard is showing mock data for missing endpoints.</p>
          <div className="mb-3">
            <strong>Error Details:</strong>
            <pre className="mt-2 p-2 bg-light border rounded" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
              {error}
            </pre>
          </div>
          <hr />
          <div className="d-flex gap-2">
            <Button variant="outline-warning" onClick={fetchDashboardData}>
              🔄 Retry All Endpoints
            </Button>
            <Button variant="outline-info" onClick={() => setError(null)}>
              ✅ Continue with Mock Data
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Custom CSS for tabs */}
      <style>{`
        .nav-tabs .nav-link {
          color: #6c757d;
          border: 1px solid transparent;
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }
        .nav-tabs .nav-link:hover {
          border-color: ${brand.primary} ${brand.primary} #dee2e6;
          color: ${brand.primary};
        }
        .nav-tabs .nav-link.active {
          color: white;
          background-color: ${brand.primary};
          border-color: ${brand.primary} ${brand.primary} #fff;
        }
        .nav-tabs {
          border-bottom: 1px solid ${brand.primary};
        }
        .nav-tabs .nav-item {
          margin-bottom: -1px;
        }
      `}</style>
      
      {/* Include Navbar */}
      <Navbar />
      
      {/* EOD Dashboard Tabs */}
      <div className="container-fluid px-4 pt-3" style={{ marginTop: '20px' }}>
        {/* Date Filter */}
        <div className="row mb-3" style={{marginTop: '20px'}}>
          <div className="col-md-3">
            <FormLabel>Select Date</FormLabel>
            <FormControl
              type="date"
              value={dateRange.from}
              onChange={(e) => {
                setDateRange({
                  from: e.target.value,
                  to: e.target.value
                });
              }}
            />
          </div>
          {/* <div className="col-md-3 d-flex align-items-end gap-2">
            <Button 
              variant="outline" 
              onClick={fetchDashboardData}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh Data'}
            </Button>
            <Button 
              variant="outline-info" 
              onClick={testEndpoints}
              disabled={loading}
              size="sm"
            >
              Test APIs
            </Button>
          </div> */}
        </div>
        
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4"
          style={{
            '--bs-nav-tabs-link-active-bg': brand.primary,
            '--bs-nav-tabs-link-active-color': 'white',
            '--bs-nav-tabs-link-hover-border-color': brand.primary,
            '--bs-nav-tabs-border-color': brand.primary,
            marginTop: '70px',
            display:'none'
          }}
        >
          <Tab eventKey="dashboard" title="Dashboard">
            <div className="p-3">
              {/* Top KPIs - 4 cards in a row */}
              <Row className="mb-4 g-3">
                <Col md={3}>
                  <div className="bg-white rounded p-3 shadow-sm border h-100">
                    <div className="text-muted small mb-1">Total Orders</div>
                    <div className="h4 mb-0 fw-bold text-dark">{totalOrders}</div>
                    <div className="text-muted small">Invoices + Returns</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="bg-white rounded p-3 shadow-sm border h-100">
                    <div className="text-muted small mb-1">Gross Sales</div>
                    <div className="h4 mb-0 fw-bold text-dark">₹ {number(grossSales)}</div>
                    <div className="text-muted small">Including diamond & gemstones</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="bg-white rounded p-3 shadow-sm border h-100">
                    <div className="text-muted small mb-1">Returns</div>
                    <div className="h4 mb-0 fw-bold text-dark">{dashboardData.salesReturns.length}</div>
                    <div className="text-muted small">Count of sales returns</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="bg-white rounded p-3 shadow-sm border h-100">
                    <div className="text-muted small mb-1">Amount Refunded</div>
                    <div className="h4 mb-0 fw-bold text-dark"> ₹ {number(dashboardData.refunds?.Total || 0)}</div>
                  </div>
                </Col>
              </Row>

              {/* Receipts vs Refunds - 2 cards in a row */}
              {/* <Row className="mb-4 g-3">
                <Col md={3}>
                  <div className="bg-white rounded p-3 shadow-sm border h-100">
                    <div className="text-muted small mb-1">Receipts (all tenders)</div>
                    <div className="h4 mb-0 fw-bold text-dark">₹ {number(receiptsTotal)}</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="bg-white rounded p-3 shadow-sm border h-100">
                    <div className="text-muted small mb-1">Net Collections</div>
                    <div className="h4 mb-0 fw-bold text-dark">₹ {number(netCollections)}</div>
                    <div className="text-muted small">Receipts − Refunds</div>
                  </div>
                </Col>
              </Row> */}

              {/* Three main summary cards */}
              <Row className="mb-4 g-3">
                <Col lg={6}>
                  <Card className="shadow-sm border-0 h-100">
                    <CardHeader className="bg-white border-bottom">
                      <CardTitle className="h6 mb-0" style={{ color: brand.primary }}>Sales Summary (by metal/stone)</CardTitle>
                    </CardHeader>
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <Table className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="border-0">Type</th>
                              <th className="border-0 text-end">Gross Wt</th>
                              <th className="border-0 text-end">Net Wt</th>
                              <th className="border-0 text-end">Orders</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboardData.salesSummary.map((m, i) => (
                              <tr key={i}>
                                <td className="fw-medium">{m.metalType || m.metal}</td>
                                <td className="text-end">{safeNumber(m.totalWeight || m.grossWt)}</td>
                                <td className="text-end">{safeNumber(m.netWeight || m.netWt)}</td>
                                <td className="text-end">{safeInteger(m.totalPieces || m.orders)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
              </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                      <CardHeader className="bg-white border-bottom">
                        <CardTitle className="h6 mb-0" style={{ color: brand.primary }}>
                          Refunds & Adjustments (Today)
                        </CardTitle>
                      </CardHeader>
                      <CardBody className="p-3">
                        {Object.entries(dashboardData.refunds || {})
                          .filter(([mode]) => mode !== "Total")
                          .map(([mode, amount]) => (
                            <div
                              key={mode}
                              className="d-flex justify-content-between align-items-center mb-2"
                            >
                              <div className="small">{mode}</div>
                              <div className="fw-semibold">₹ {number(amount)}</div>
                            </div>
                          ))}

                        <hr className="my-2" />

                        <div className="d-flex justify-content-between align-items-center">
                          <div className="small">Total Refunded</div>
                          <div className="fw-semibold">
                            ₹ {number(dashboardData.refunds?.Total || 0)}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
              </Row>
              <Row className="mb-4 g-3">
                <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                      <CardHeader className="bg-white border-bottom">
                        <CardTitle className="h6 mb-0" style={{ color: brand.primary }}>
                          Customer Balance
                        </CardTitle>
                      </CardHeader>
                      <CardBody className="p-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Particulars</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>Opening Balance</td>
                              <td className="text-end">₹ {number(dashboardData.customerBalance?.opening || 0)}</td>
                            </tr>
                            <tr>
                              <td>Balance Given</td>
                              <td className="text-end">₹ {number(dashboardData.customerBalance?.given || 0)}</td>
                            </tr>
                            <tr>
                              <td>Balance Received</td>
                              <td className="text-end">₹ {number(dashboardData.customerBalance?.received || 0)}</td>
                            </tr>
                            <tr>
                              <td>Discount Given</td>
                              <td className="text-end">₹ {number(dashboardData.customerBalance?.discount || 0)}</td>
                            </tr>
                            <tr>
                              <td><strong>Bal Payable</strong></td>
                              <td className="text-end fw-semibold">₹ {number(dashboardData.customerBalance?.payable || 0)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </CardBody>
                    </Card>
                  </Col>
                    <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <CardHeader className="bg-white border-bottom">
                          <CardTitle className="h6 mb-0" style={{ color: brand.primary }}>
                            Order Advance
                          </CardTitle>
                        </CardHeader>
                        <CardBody className="p-3">
                          <table className="table table-bordered table-sm mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Particulars</th>
                                <th className="text-end">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Opening Advance</td>
                                <td className="text-end"> ₹ {number(dashboardData.advanceSummary?.find(i => i.label === "Opening Advance")?.amount || 0)}</td>
                              </tr>
                              <tr>
                                <td>Advance Receive</td>
                                <td className="text-end">₹ {number(dashboardData.advanceSummary?.find(i => i.label === "Advance Receive")?.amount || 0)}</td>
                              </tr>
                              <tr>
                                <td>Advance Issue</td>
                                <td className="text-end">₹ {number(dashboardData.advanceSummary?.find(i => i.label === "Advance Issue")?.amount || 0)}</td>
                              </tr>
                              <tr>
                                <td><strong>Closing Advance</strong></td>
                                <td className="text-end fw-semibold">₹ {number(dashboardData.advanceSummary?.find(i => i.label === "Closing Advance")?.amount || 0)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                  </Col>
              </Row>
              
                  <Card className="shadow-sm border-0 h-100 mb-4">
                    <CardHeader className="bg-white border-bottom">
                      <CardTitle className="h6 mb-0" style={{ color: brand.primary }}>URD / Old Gold Summary</CardTitle>
                    </CardHeader>
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <Table className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="border-0">Rate</th>
                              <th className="border-0 text-end">M.Wt</th>
                              <th className="border-0 text-end">Stn.Wt</th>
                              <th className="border-0 text-end">Net Wt</th>
                              <th className="border-0 text-end">Eqv. Wt</th>
                              <th className="border-0 text-end">Amc</th>
                              <th className="border-0 text-end">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboardData.urdSummary.map((u, i) => (
                              <tr key={i}>
                                <td>{u.rate}</td>
                                <td className="text-end">{safeNumber(u.mWeight)}</td>
                                <td className="text-end">{safeNumber(u.stnWt)}</td>
                                <td className="text-end">{safeNumber(u.netWt)}</td>
                                <td className="text-end">{safeNumber(u.eqWt)}</td>
                                <td className="text-end">₹ {safeNumber(u.amount)}</td>
                                <td className="text-end">{u.transaction_status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
              </div>
                    </CardBody>
                  </Card>
              {/* Sales Return Summary */}
              <Card className="border-0 shadow-sm mb-4">
                <CardHeader className="bg-white border-bottom">
                  <CardTitle className="h6 d-flex align-items-center gap-2 mb-0" style={{ color: brand.primary }}>
                    <FaUndo /> Sales Return Summary
                  </CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <Table className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0">SR No</th>
                          <th className="border-0">Ref Invoice</th>
                          <th className="border-0">Item</th>
                          <th className="border-0 text-end">Gross Wt</th>
                          <th className="border-0 text-end">Net Wt</th>
                          <th className="border-0 text-end">Return Amt</th>
                          <th className="border-0">Refund Mode</th>
                          <th className="border-0 text-end">Refunded</th>
                          <th className="border-0 text-end">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.salesReturns.map((r, i) => (
                          <tr key={i}>
                            <td className="font-monospace small">{r.srNo}</td>
                            <td className="font-monospace small">{r.refNo}</td>
                            <td className="text-nowrap">{r.type}</td>
                            <td className="text-end">{safeNumber(r.grossWt)}</td>
                            <td className="text-end">{safeNumber(r.netWt)}</td>
                            <td className="text-end">₹ {number(r.amount || 0)}</td>
                            <td>{r.refundMode}</td>
                            <td className="text-end">₹ {number(r.refundAmount || 0)}</td>
                            <td className="text-end">
                              <Badge bg="success" className="d-flex align-items-center gap-1 w-auto">
                                <FaCheckCircle className="small" /> {r.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>

              {/* Exceptions & Alerts */}
              <Card className="border-0 shadow-sm d-none">
                <CardHeader className="bg-white border-bottom">
                  <CardTitle className="h6 d-flex align-items-center gap-2 mb-0" style={{ color: brand.primary }}>
                    <FaExclamationTriangle className="text-warning" /> Exceptions to Review
                  </CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <Table className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0">Type</th>
                          <th className="border-0">Reference</th>
                          <th className="border-0">By</th>
                          <th className="border-0">Notes</th>
                          <th className="border-0 text-end">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.exceptions.map((e) => (
                          <tr key={e.id}>
                            <td className="text-nowrap">{e.type}</td>
                            <td className="font-monospace small">{e.reference || e.ref}</td>
                            <td>{e.user || e.by}</td>
                            <td className="small">{e.note}</td>
                            <td className="text-end">
                              <Badge bg={e.severity === "high" ? "danger" : "warning"} className="d-flex align-items-center gap-1 w-auto">
                                <FaExclamationTriangle className="small" /> {e.severity === "high" ? "Needs audit" : "Check"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Tab>

          <Tab eventKey="reconcile" title="Reconcile">
            <div className="p-3">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="h6" style={{ color: brand.primary }}>EOD Checklist</CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      <div className="rounded p-3 bg-light mb-3">
                        <SectionTitle 
                          title="Cash Drawer" 
                          right={<StatusPill ok={dashboardData.reconciliation.cashDrawer?.status === 'matched'} label={dashboardData.reconciliation.cashDrawer?.status || 'Unknown'}/>} 
                        />
                        <div className="small">
                          Counted ₹{number(dashboardData.reconciliation.cashDrawer?.counted || 0)} • 
                          System ₹{number(dashboardData.reconciliation.cashDrawer?.system || 0)}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="rounded p-3 bg-light mb-3">
                        <SectionTitle 
                          title="Card Settlements" 
                          right={<StatusPill ok={dashboardData.reconciliation.cardSettlements?.status === 'ok'} label={dashboardData.reconciliation.cardSettlements?.status || 'Unknown'}/>} 
                        />
                        <div className="small">
                          Visa/Master ₹{number(dashboardData.reconciliation.cardSettlements?.visaMaster || 0)} • 
                          POS batch {dashboardData.reconciliation.cardSettlements?.posBatchClosed ? 'closed' : 'open'}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="rounded p-3 bg-light mb-3">
                        <SectionTitle 
                          title="UPI / NEFT" 
                          right={<StatusPill ok={dashboardData.reconciliation.upiTransactions?.status === 'ok'} label={dashboardData.reconciliation.upiTransactions?.status || 'Unknown'}/>} 
                        />
                        <div className="small">
                          ₹{number(dashboardData.reconciliation.upiTransactions?.totalAmount || 0)} received across {dashboardData.reconciliation.upiTransactions?.transactionCount || 0} txns
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="rounded p-3 bg-light mb-3">
                        <SectionTitle title="Refunds Processed" right={<StatusPill ok={true} label="Tallied"/>} />
                        <div className="small">
                          Card: ₹{number(totalRefunded * 0.7)} • Cash: ₹{number(totalRefunded * 0.3)} • UPI: ₹0
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="rounded p-3 bg-light mb-3">
                        <SectionTitle title="Metal Weights" right={<StatusPill ok={true} label="Balanced"/>} />
                        <div className="small">
                          22K: -0.00g variance • Silver: -0.00g
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="rounded p-3 bg-light mb-3">
                        <SectionTitle title="Stones & Diamonds" right={<StatusPill ok={false} label="Variance"/>} />
                        <div className="small">
                          Diamonds: -0.06ct (to review); Emeralds: OK
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter className="d-flex justify-content-end gap-2">
                  <Button variant="outline">
                    <FaFilter className="me-1" />View Mismatches
                </Button>
                  <Button style={{ background: brand.primary, border: 'none' }}>
                    <FaCheckCircle className="me-1" />Lock EOD
                </Button>
                </CardFooter>
              </Card>
              </div>
          </Tab>

          <Tab eventKey="audit" title="Audit Log">
            <div className="p-3">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="h6" style={{ color: brand.primary }}>Live Activity Log (time-wise)</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <FormControl placeholder="Search action, user, SKU, ref…" className="w-auto" />
                    <Form.Select className="w-auto">
                      <option value="all">All Types</option>
                      <option value="sale">Sale</option>
                      <option value="return">Return</option>
                      <option value="refund">Refund</option>
                      <option value="payment">Payment</option>
              </Form.Select>
            </div>

                  {/* Timeline */}
                  <div className="position-relative ps-4">
                    <div 
                      className="position-absolute top-0 bottom-0 start-0"
                      style={{ width: '2px', background: brand.primary + '55' }}
                    />
                    <div className="space-y-3">
                      {dashboardData.actionLog.map((a, i) => (
                        <div key={i} className="d-flex gap-3">
                          <div className="position-relative">
                            <div 
                              className="rounded-circle mt-1"
                              style={{ width: '16px', height: '16px', background: brand.primary }}
                            />
                          </div>
                          <div className="flex-fill rounded p-3 bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center gap-2 small">
                                <span className="font-monospace">{a.timestamp || a.t}</span>
                                <Badge style={{ background: brand.primary }}>{a.action || a.kind}</Badge>
                                <span className="text-muted">{a.user} • {a.role}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2 text-muted">
                                <Icon name={a.icon} />
                                <span className="font-monospace small">{a.reference || a.ref}</span>
                              </div>
                            </div>
                            <div className="mt-1 small">{a.description || a.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Tab>

          <Tab eventKey="report" title="Daily Report">
            <div className="p-3">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="h6" style={{ color: brand.primary }}>Daily Report – Summary</CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col lg={4}>
                      <div className="rounded p-3 bg-light">
                        <SectionTitle title="Sales by Category" />
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Gold Jewellery</span>
                          <strong className="text-dark">₹ {number(312550)}</strong>
                        </div>
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Diamond Jewellery</span>
                          <strong className="text-dark">₹ {number(218360)}</strong>
                        </div>
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Gemstones</span>
                          <strong className="text-dark">₹ {number(163150)}</strong>
                        </div>
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Silver</span>
                          <strong className="text-dark">₹ {number(125658)}</strong>
                        </div>
                        <hr />
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Average Ticket Size</span>
                          <strong className="text-dark">₹ {number(15420)}</strong>
                        </div>
                        <div className="small d-flex justify-content-between">
                          <span className="text-dark">Return Rate</span>
                          <strong className="text-dark">{((dashboardData.salesReturns.length / totalOrders) * 100).toFixed(1)}%</strong>
                        </div>
                      </div>
                    </Col>

                    <Col lg={4}>
                      <div className="rounded p-3 bg-light">
                        <SectionTitle title="Payments, Refunds & Dues" />
                        {dashboardData.tenderSummary.map((t, i) => (
                          <div key={i} className="small d-flex justify-content-between mb-1">
                            <span className="text-dark">{t.mode}</span>
                            <strong className="text-dark">₹ {number(t.amount || 0)}</strong>
                          </div>
                        ))}
                        <hr />
                        <div className="small fw-medium mb-1">Refunds by Mode</div>
                        {dashboardData.salesReturns.map((r, i) => (
                          <div key={i} className="small d-flex justify-content-between mb-1">
                            <span className="text-dark">{r.refundMode}</span>
                            <strong className="text-dark">₹ {number(r.refundAmount)}</strong>
                          </div>
                        ))}
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Total Refunded</span>
                          <strong className="text-dark">₹ {number(totalRefunded)}</strong>
                        </div>
                        <hr />
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Net Collections</span>
                          <strong className="text-dark">₹ {number(netCollections)}</strong>
                        </div>
                        <div className="small d-flex justify-content-between">
                          <span className="text-dark">Customer Dues (closing)</span>
                          <strong className="text-dark">₹ {number(58739)}</strong>
                        </div>
                      </div>
                    </Col>

                    <Col lg={4}>
                      <div className="rounded p-3 bg-light">
                        <SectionTitle title="Operations" />
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Repairs / Resizes logged</span>
                          <strong className="text-dark">7</strong>
                        </div>
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Stone Settings done</span>
                          <strong className="text-dark">4</strong>
                        </div>
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">GRNs (new inventory)</span>
                          <strong className="text-dark">2</strong>
                        </div>
                        <div className="small d-flex justify-content-between mb-1">
                          <span className="text-dark">Approvals above 10% discount</span>
                          <strong className="text-dark">3</strong>
                        </div>
                        <hr />
                        <div className="small d-flex justify-content-between">
                          <span className="text-dark">Gold Rate (22K)</span>
                          <strong className="text-dark">₹ 6,650 / g</strong>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardFooter className="d-flex justify-content-end gap-2">
                  <Button style={{ background: brand.primary, border: 'none' }}>
                    <FaPrint className="me-1" />Print Report
                </Button>
                  <Button variant="outline">
                    <FaDownload className="me-1" />Export PDF
                </Button>
                </CardFooter>
              </Card>
              </div>
          </Tab>

          <Tab eventKey="settings" title={<><FaCog className="me-1" />Settings</>}>
            <div className="p-3">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="h6" style={{ color: brand.primary }}>Configuration</CardTitle>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={3}>
                      <div className="mb-3">
                        <FormLabel>High Discount Threshold (%)</FormLabel>
                        <FormControl defaultValue={15} />
            </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <FormLabel>Variance Alert – Diamond (ct)</FormLabel>
                        <FormControl defaultValue={0.05} />
          </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <FormLabel>Variance Alert – Gold (g)</FormLabel>
                        <FormControl defaultValue={0.1} />
        </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <FormLabel>Refunds require approval above (₹)</FormLabel>
                        <FormControl defaultValue={10000} />
                      </div>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="outline">Reset</Button>
                    <Button style={{ background: brand.primary, border: 'none' }}>Save</Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default EODDashboard;
